import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { computed, signal } from '@preact/signals-core';
import { Subject } from 'rxjs';
import type { Doc, Transaction } from 'yjs';

import { nanoid } from '../../utils/id-generator';
import {
  assistantPresenceKey,
  type AssistantPresenceState,
  type DatabasePlace,
  presenceOwnerClientId,
  readAssistantPresence,
  readDatabasePlace,
  type StackItem,
} from '../../yjs';
import { StoreExtension } from '../store-extension';
import { type AgentPlayback, agentPlaybackOf } from './agent-playback';
import {
  type AgentPointer,
  agentPointerKey,
  agentPointerOf,
  isAgentOrigin,
} from './agent-pointers';
import type { BaseSelection } from './base';
import { SelectionIdentifier } from './identifier';
import {
  REMOTE_TEXT_SELECTION_GRACE_MS,
  type RemoteTextSelectionCache,
  stabilizeRemoteTextSelections,
} from './remote-selection-stabilizer';
import type { SelectionConstructor } from './types';

/** The assistant of this client's user, taking part in the document. */
export interface AssistantSelectionHandle {
  /** From here on, what this editor selects is the assistant's doing. */
  takeHand(): void;
  /** The user acts again: what this editor selects is theirs. */
  returnHand(): void;
  /** What the assistant marks while the editor's own selection stays put. */
  show(selections: BaseSelection[]): void;
  /** The row or cell of a database the assistant works on; null when it is done there. */
  work(place: DatabasePlace | null): void;
  /**
   * Where the assistant's pointer is, as a cursor selection of the canvas;
   * null when it is not over this editor.
   */
  point(cursor: BaseSelection | null): void;
  /** The assistant is done in this document. */
  leave(): void;
}

interface AdmittedAssistant {
  label: string;
  hand: boolean;
  shown: BaseSelection[];
  /** Where in a database the assistant works, as this editor named it. */
  place: DatabasePlace | null;
  /** The assistant's own pointer: what it shows, never the editor's cursor. */
  cursor: BaseSelection | null;
  /** JSON of the place this editor published last: only that one is its to take away. */
  namedPlace: string | null;
  /** JSON of what was published last, to skip writes that change nothing. */
  published: string | null;
}

export class StoreSelectionExtension extends StoreExtension {
  static override readonly key = 'selection';

  private readonly _id = `${this.store.id}:${nanoid()}`;
  private _selectionConstructors: Record<string, SelectionConstructor> = {};
  private readonly _selections = signal<BaseSelection[]>([]);
  private readonly _remoteSelections = signal<Map<number, BaseSelection[]>>(
    new Map()
  );
  private readonly _remoteTextSelectionCache: RemoteTextSelectionCache =
    new Map();
  // Remote clients on an older selection contract send payloads this client
  // cannot parse on every awareness change. Each client/type pair is logged
  // once and counted afterwards, so the failure stays visible without spam.
  private readonly _remoteSelectionParseFailures = new Map<string, number>();
  private _remoteTextSelectionExpiryTimer: ReturnType<
    typeof setTimeout
  > | null = null;
  private _assistant: AdmittedAssistant | null = null;
  private readonly _agentPointers = signal<ReadonlyMap<string, AgentPointer>>(
    new Map()
  );
  // Keep ended runs for this store's lifetime: a late update of one is shown
  // once and its pointer leaves at once, so it never stays.
  private readonly _endedAgentRuns = new Set<string>();

  /**
   * A crewmate's live change moves its pointer through what it changed here,
   * in the order of its playback.
   */
  private readonly _agentTransaction = (transaction: Transaction) => {
    const origin = transaction.origin;
    if (!isAgentOrigin(origin) || transaction.changed.size === 0) return;
    const named = agentPointerOf(origin, this.store.id);
    if (!named) return;
    const playback = agentPlaybackOf(
      transaction,
      this.store.pageStructure,
      origin,
      named
    );
    const pointer = {
      ...named,
      targets: playback.steps.map(step => step.target),
    };
    const pointers = new Map(this._agentPointers.value);
    pointers.set(agentPointerKey(pointer), pointer);
    this._agentPointers.value = pointers;
    this.slots.agentPointersChanged.next(pointers);
    this.slots.agentPlayback.next(playback);
    // A change can reach this document after its run's end (a database row
    // shard syncs after the terminal event): shown, its pointer leaves.
    if (this._endedAgentRuns.has(origin.runId))
      this._removeRunPointers(origin.runId);
  };
  /** Documents of this page outside its parts, with how often each is observed. */
  private readonly _agentDocs = new Map<Doc, number>();

  /**
   * A document of this page outside its parts (a database row shard): its
   * agent updates move the pointers here like those of a part. Every caller
   * releases its own observation.
   */
  observeAgentDoc(doc: Doc): () => void {
    const count = this._agentDocs.get(doc) ?? 0;
    this._agentDocs.set(doc, count + 1);
    if (count === 0) doc.on('afterTransaction', this._agentTransaction);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (this._agentDocs.get(doc) ?? 1) - 1;
      if (left > 0) {
        this._agentDocs.set(doc, left);
        return;
      }
      this._agentDocs.delete(doc);
      doc.off('afterTransaction', this._agentTransaction);
    };
  }

  /** The own assistant as last seen in awareness; a new object means it changed. */
  private _seenLocalAssistant: unknown = null;

  private readonly _itemAdded = (event: { stackItem: StackItem }) => {
    event.stackItem.meta.set('selection-state', this._selections.value);
  };

  private readonly _itemPopped = (event: { stackItem: StackItem }) => {
    const selection = event.stackItem.meta.get('selection-state');
    if (selection) {
      this.set(selection as BaseSelection[]);
    }
  };

  private readonly _jsonToSelection = (json: Record<string, unknown>) => {
    const ctor = this._selectionConstructors[json.type as string];
    if (!ctor) {
      throw new BlockSuiteError(
        ErrorCode.SelectionError,
        `Unknown selection type: ${json.type}`
      );
    }
    return ctor.fromJSON(json);
  };

  private readonly _awarenessChangeHandler = (change: {
    updated: number[];
    added: number[];
    removed: number[];
  }) => {
    const all = change.updated.concat(change.added).concat(change.removed);
    const localClientID = this.store.awarenessStore.awareness.clientID;
    const exceptLocal = all.filter(id => id !== localClientID);
    const localAssistant =
      this.store.awarenessStore.getLocalState()?.assistant ?? null;
    const localAssistantChanged = localAssistant !== this._seenLocalAssistant;
    this._seenLocalAssistant = localAssistant;

    // Other clients, and the own assistant, which takes part like one of them.
    if (exceptLocal.length > 0 || localAssistantChanged) {
      this._emitRemoteSelections();
    }
  };

  private _parsePublishedSelections(
    presenceKey: number,
    published: Record<string, Array<Record<string, unknown>>>
  ): BaseSelection[] {
    // selection id starts with the same block collection id from others clients would be considered as remote selections
    return Object.entries(published)
      .filter(([key]) => key.startsWith(this.store.id))
      .flatMap(([_, selection]) => selection)
      .map(json => {
        try {
          return this._jsonToSelection(json);
        } catch (error) {
          this._recordRemoteSelectionParseFailure(presenceKey, json, error);
          return null;
        }
      })
      .filter((sel): sel is BaseSelection => !!sel);
  }

  private _collectRemoteSelections(): Map<number, BaseSelection[]> {
    const map = new Map<number, BaseSelection[]>();
    const states = this.store.awarenessStore.getStates();
    const localClientID = this.store.awarenessStore.awareness.clientID;
    states.forEach((state, id) => {
      const local = id === localClientID;
      if (!local) {
        map.set(
          id,
          this._parsePublishedSelections(id, state.selectionV2 ?? {})
        );
      }
      // An assistant takes part next to its owner, under a key of its own.
      const assistant = readAssistantPresence(state);
      if (!assistant) return;
      const key = assistantPresenceKey(id);
      const selections = this._parsePublishedSelections(
        key,
        assistant.selections
      );
      // The owner's app draws its own assistant's pointer itself.
      map.set(
        key,
        local
          ? selections.filter(selection => selection.type !== 'cursor')
          : selections
      );
    });
    for (const key of this._remoteSelectionParseFailures.keys()) {
      if (!states.has(presenceOwnerClientId(Number(key.split(':')[0])))) {
        this._remoteSelectionParseFailures.delete(key);
      }
    }
    return map;
  }

  private _recordRemoteSelectionParseFailure(
    clientId: number,
    json: Record<string, unknown>,
    error: unknown
  ): void {
    const key = `${clientId}:${String(json.type)}`;
    const count = (this._remoteSelectionParseFailures.get(key) ?? 0) + 1;
    this._remoteSelectionParseFailures.set(key, count);
    if (count === 1) {
      console.error(
        'Parse remote selection failed; further failures of this client and selection type are only counted:',
        clientId,
        json,
        error
      );
    }
  }

  /** Parse failures per `presenceKey:selectionType` of remote clients still present in awareness. */
  get remoteSelectionParseFailures(): ReadonlyMap<string, number> {
    return this._remoteSelectionParseFailures;
  }

  private _emitRemoteSelections(): void {
    if (this._remoteTextSelectionExpiryTimer) {
      clearTimeout(this._remoteTextSelectionExpiryTimer);
      this._remoteTextSelectionExpiryTimer = null;
    }
    const now = performance.now();
    const stabilized = stabilizeRemoteTextSelections({
      cache: this._remoteTextSelectionCache,
      now,
      selections: this._collectRemoteSelections(),
    });
    this._remoteSelections.value = stabilized.selections;
    this.slots.remoteChanged.next(stabilized.selections);

    if (stabilized.nextExpiryAt !== null) {
      const delay = Math.max(0, stabilized.nextExpiryAt - now) + 1;
      this._remoteTextSelectionExpiryTimer = setTimeout(
        () => this._emitRemoteSelections(),
        Math.min(delay, REMOTE_TEXT_SELECTION_GRACE_MS + 1)
      );
    }
  }

  slots = {
    changed: new Subject<BaseSelection[]>(),
    remoteChanged: new Subject<Map<number, BaseSelection[]>>(),
    agentPointersChanged: new Subject<ReadonlyMap<string, AgentPointer>>(),
    /** The changes of each agent update here, in the order to show them. */
    agentPlayback: new Subject<AgentPlayback>(),
    /** Every terminal run event, including runs without a pointer here. */
    agentRunEnded: new Subject<string>(),
  };

  /** The crewmates working in this document, by owner and run. */
  get agentPointers(): ReadonlyMap<string, AgentPointer> {
    return this._agentPointers.value;
  }

  /** A run ended: its pointer leaves the document. */
  endAgentRun(runId: string): void {
    this._endedAgentRuns.add(runId);
    this._removeRunPointers(runId);
    this.slots.agentRunEnded.next(runId);
  }

  private _removeRunPointers(runId: string): void {
    const pointers = new Map(this._agentPointers.value);
    let removed = false;
    for (const [key, pointer] of pointers)
      if (pointer.runId === runId) removed = pointers.delete(key) || removed;
    if (removed) {
      this._agentPointers.value = pointers;
      this.slots.agentPointersChanged.next(pointers);
    }
  }

  private _releaseAgentParts?: () => void;

  override loaded() {
    this.store.provider.getAll(SelectionIdentifier).forEach(ctor => {
      [ctor].flat().forEach(ctor => {
        this._selectionConstructors[ctor.type] = ctor;
      });
    });

    this.store.awarenessStore.awareness.on(
      'change',
      this._awarenessChangeHandler
    );
    this._emitRemoteSelections();
    this._releaseAgentParts = this.store.pageStructure.onPartTransaction(
      this._agentTransaction
    );

    this.store.history.undoManager.on('stack-item-added', this._itemAdded);
    this.store.history.undoManager.on('stack-item-popped', this._itemPopped);
  }

  override disposed() {
    super.disposed();
    this._dismissAssistant();
    if (this._remoteTextSelectionExpiryTimer) {
      clearTimeout(this._remoteTextSelectionExpiryTimer);
      this._remoteTextSelectionExpiryTimer = null;
    }
    this._remoteTextSelectionCache.clear();
    this._releaseAgentParts?.();
    this._releaseAgentParts = undefined;
    for (const doc of this._agentDocs.keys())
      doc.off('afterTransaction', this._agentTransaction);
    this._agentDocs.clear();
    this.store.awarenessStore.awareness.off(
      'change',
      this._awarenessChangeHandler
    );
    this.store.history.undoManager.off('stack-item-added', this._itemAdded);
    this.store.history.undoManager.off('stack-item-popped', this._itemPopped);
  }

  get value() {
    return this._selections.value;
  }

  get remoteSelections() {
    return this._remoteSelections.value;
  }

  clear(types?: string[]) {
    if (types) {
      const values = this.value.filter(
        selection => !types.includes(selection.type)
      );
      this.set(values);
    } else {
      this.set([]);
    }
  }

  create<T extends SelectionConstructor>(
    Type: T,
    ...args: ConstructorParameters<T>
  ): InstanceType<T> {
    return new Type(...args) as InstanceType<T>;
  }

  getGroup(group: string) {
    return this.value.filter(s => s.group === group);
  }

  filter<T extends SelectionConstructor>(type: T) {
    return this.filter$(type).value;
  }

  filter$<T extends SelectionConstructor>(type: T) {
    return computed(() =>
      this.value.filter((sel): sel is InstanceType<T> => sel.is(type))
    );
  }

  find<T extends SelectionConstructor>(type: T) {
    return this.find$(type).value;
  }

  find$<T extends SelectionConstructor>(type: T) {
    return computed(() =>
      this.value.find((sel): sel is InstanceType<T> => sel.is(type))
    );
  }

  set(selections: BaseSelection[]) {
    this._selections.value = selections;
    this._publish(selections);
    this.slots.changed.next(selections);
  }

  /** Tells the others what this editor selects: as the user, or as their assistant. */
  private _publish(selections: BaseSelection[]): void {
    if (this._assistant?.hand) {
      this._publishAssistant(selections);
      return;
    }
    this.store.awarenessStore.setLocalSelection(
      this._id,
      selections.map(s => s.toJSON())
    );
  }

  private _publishAssistant(
    editorSelections: BaseSelection[] = this._selections.value
  ): void {
    const assistant = this._assistant;
    if (!assistant) return;
    const awarenessStore = this.store.awarenessStore;
    const state = awarenessStore.getLocalState();
    // Without a local state this client has left the document.
    if (!state) return;
    // The editor's cursor is the user's pointer, the assistant has its own.
    const marked = [
      ...(assistant.hand
        ? editorSelections.filter(selection => selection.type !== 'cursor')
        : assistant.shown),
      ...(assistant.cursor ? [assistant.cursor] : []),
    ];
    const selection = marked.map(s => s.toJSON());
    const published = JSON.stringify([
      assistant.label,
      selection,
      assistant.place,
    ]);
    if (published === assistant.published) return;
    assistant.published = published;
    const current = readAssistantPresence(state);
    const databasePlace =
      assistant.place ?? this._placeOfOthers(assistant, current);
    assistant.namedPlace = assistant.place
      ? JSON.stringify(assistant.place)
      : null;
    awarenessStore.setLocalStateField('assistant', {
      label: assistant.label,
      selections: { ...current?.selections, [this._id]: selection },
      ...(databasePlace ? { databasePlace } : {}),
    });
  }

  /** A published place this editor did not name is another editor's and stays. */
  private _placeOfOthers(
    assistant: AdmittedAssistant,
    current: AssistantPresenceState | null
  ): DatabasePlace | undefined {
    const written = current?.databasePlace;
    return written && JSON.stringify(written) !== assistant.namedPlace
      ? written
      : undefined;
  }

  private _dismissAssistant(): void {
    const assistant = this._assistant;
    if (!assistant) return;
    this._assistant = null;
    const awarenessStore = this.store.awarenessStore;
    const state = awarenessStore.getLocalState();
    if (!state) return;
    if (assistant.hand) {
      awarenessStore.setLocalSelection(
        this._id,
        this._selections.value.map(s => s.toJSON())
      );
    }
    // Another editor of this document may still show the assistant.
    const current = readAssistantPresence(awarenessStore.getLocalState());
    const { [this._id]: _own, ...others } = current?.selections ?? {};
    const databasePlace = this._placeOfOthers(assistant, current);
    awarenessStore.setLocalStateField(
      'assistant',
      Object.keys(others).length > 0
        ? {
            label: assistant.label,
            selections: others,
            ...(databasePlace ? { databasePlace } : {}),
          }
        : null
    );
  }

  /**
   * Lets the user's assistant take part in this document under `label`. It is
   * shown to everyone like a further person: with a pointer, with what it marks
   * and, while it has the hand, with what this editor selects and types.
   */
  admitAssistant(label: string): AssistantSelectionHandle {
    this._dismissAssistant();
    const assistant: AdmittedAssistant = {
      label,
      hand: false,
      shown: [],
      cursor: null,
      place: null,
      namedPlace: null,
      published: null,
    };
    this._assistant = assistant;
    this._publishAssistant();
    const admitted = () => this._assistant === assistant;
    return {
      takeHand: () => {
        if (!admitted() || assistant.hand) return;
        assistant.hand = true;
        // The user's own selection rests; their pointer stays where it was.
        this.store.awarenessStore.setLocalSelection(
          this._id,
          this.store.awarenessStore
            .getLocalSelection(this._id)
            .filter(selection => selection.type === 'cursor')
        );
        this._publishAssistant();
      },
      returnHand: () => {
        if (!admitted() || !assistant.hand) return;
        assistant.hand = false;
        this._publish(this._selections.value);
        this._publishAssistant();
      },
      show: selections => {
        if (!admitted()) return;
        assistant.shown = selections;
        this._publishAssistant();
      },
      work: place => {
        if (!admitted()) return;
        assistant.place = readDatabasePlace(place) ?? null;
        this._publishAssistant();
      },
      point: cursor => {
        if (!admitted()) return;
        assistant.cursor = cursor;
        this._publishAssistant();
      },
      leave: () => {
        if (admitted()) this._dismissAssistant();
      },
    };
  }

  setGroup(group: string, selections: BaseSelection[]) {
    const current = this.value.filter(s => s.group !== group);
    this.set([...current, ...selections]);
  }

  // This method is used to clear **current editor's remote selections**
  // When the editor is not active, the remote selections should be cleared
  // So other editors won't see the remote selections from this editor
  clearRemote(types?: string[]) {
    this._publish(
      types
        ? this.value.filter(selection => !types.includes(selection.type))
        : []
    );
  }

  update(fn: (currentSelections: BaseSelection[]) => BaseSelection[]) {
    const selections = fn(this.value);
    this.set(selections);
  }

  fromJSON(json: Record<string, unknown>[]) {
    const selections = json.map(json => {
      return this._jsonToSelection(json);
    });
    return this.set(selections);
  }
}
